import {SINGLE_DATASET_INPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {GetAnalysisPipelineStep, GetAnalysisStepData} from 'analysis-tool/AnalysisTypes';
import {DataReference} from 'analysis-tool/ui/main-view/MainViewTypes';
import {assertAndContinue} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isStepSucceeded} from 'pipelines/PipelineStepStatusUtils';

import {isViewTerminalStepType} from './LineageStepTypes';

export interface StepReferencesForVisualizationActions {
  precedingStep: GetAnalysisPipelineStep;
  outputDataReferences?: Array<DataReference>;
  precedingStepData: GetAnalysisStepData;
}

/**
 * For transforms that are initiated through the DataFrameTableWrapper, we need to know the preceding step and
 * its output in order to add the transform to the correct place in the pipeline. We allow users to edit the
 * last transform step in the view instead of adding a new step and use this function to automatically
 * determine whether we will append to a previous step or create a new step.
 */
export function getStepReferencesForViewActions({
  viewSteps,
  pipelineStepType,
}: {
  viewSteps: Array<GetAnalysisPipelineStep>;
  pipelineStepType: PipelineStepType;
}): StepReferencesForVisualizationActions | undefined {
  const lastStepInView = viewSteps[viewSteps.length - 1];
  const terminalViewStep = isViewTerminalStepType(lastStepInView.definition.stepType) ? lastStepInView : null;
  const precedingStep = terminalViewStep == null ? lastStepInView : viewSteps[viewSteps.length - 2];

  if (!isStepSucceeded(precedingStep.statusCode)) {
    return undefined;
  }

  assertAndContinue(
    precedingStep.outputStepDatas.length === 1,
    precedingStep.definition.stepType === pipelineStepType
      ? `Expected ${pipelineStepType} step to have a single output step data.`
      : `Expected the step preceding a ${pipelineStepType} step in a visualization view lineage to have a single output step data.`
  );

  const outputDataReferences =
    terminalViewStep == null
      ? undefined
      : [
          {
            stepId: terminalViewStep.id,
            stepDataName: SINGLE_DATASET_INPUT_STEP_DATA_NAME,
          },
        ];
  const precedingStepData = precedingStep.outputStepDatas[0];
  return {
    precedingStep,
    outputDataReferences,
    precedingStepData,
  };
}
